// プレイヤーの状態遷移ロジック。
// Phase 4.18:
//  - スタミナ管理: 攻撃/回避/パリィにコスト、不足時は発動不可
//  - 空振りパリィ: 攻撃を吸収せず終わると軽い硬直のみ (parrying を wastedParryStiffnessMs 延長・連打抑止)。体幹は増やさない
//  - 入力優先度: parry > step > attack (足りない時は発動しないだけ)

import { PULL, THRUST, pullStepBackPx, pullTimingOn, thrustTimingOn } from './luna_exec_swap'
import type { Boss, Player, PlayerGrabState } from './types'
import type { Input } from './input'
import { consume } from './input'
import { arenaBoundsFor, BOSS_CORE, EXECUTE, FIXED_DT, PLAYER, executeApproachRangeFor, executeReachFor } from './balance'
import { bossCoreHalfWidth, bossCounterStopGap } from './bosses'
import { beastBody, stanceBodyGap, stanceDodgeLanding } from './boss_body_envelope'
import { guardEnabled } from './guard'
import { parryOneCatchMs, parryOneOn, parryOneTailMs } from './parry_one'
import { playerComboEnabled } from './player_combo_switch'
import { attackStepTiming, attackTimingOf, heavyLungeProgress } from './player_attack_timing'
import { swingFacingLocked } from './swing_facing_trial'
import { playerIncomingDamage, playerParryActiveMs } from './difficulty'
import { dodgeTravelMs, dodgeTravelProgress, playerStepIFrameMs, stepDurationMs, walkSpeedPxPerSec } from './player_locomotion_trial'
import { lunaWalkAccel, nextWalkVelocity } from './player_walk_new'
import { planExecuteSpacing, planFinisherSpacing, spacingPrepProgress } from './execute_spacing'
import { EXEC_PARRY, execPushSliding, execStunActive, isBossFinisherAttack, stepExecPush, stunParryAccepts } from './exec_parry'

// 介錯C-2採用版。通常起動は修正版、DEV URLで旧版との比較を残す。
export type CoreLandingMode = 'legacy' | 'settle'
let coreLandingMode: CoreLandingMode = 'settle'
export function setCoreLandingMode(mode: CoreLandingMode): void { coreLandingMode = mode }

// 回避終了後の押し出しを、回避中の着地点調整へ置き換える。
// 壁の外や逆方向へは補正しない。通常の90pxで体外へ出られる場合も補正しない。
function coreStepLanding(fromX: number, dir: number, boss: Boss): number | undefined {
  if (coreLandingMode !== 'settle' || boss.id !== 'the-executioner' || !bossCoreEnabled) return undefined
  const normal = clampToArena(fromX + dir * PLAYER.stepDistance, boss.id)
  const gap = bossCoreHalfWidth(boss) + BOSS_CORE.playerHalfWidth
  if (Math.abs(normal - boss.x) >= gap) return undefined
  const side = Math.sign(normal - boss.x) || dir
  // 向こう側が壁の外なら手前で止まる。壁とボスの間へ自動で送り込まない。
  for (const target of [boss.x + side * gap, boss.x - side * gap]) {
    if (target === clampToArena(target, boss.id) && (target - fromX) * dir >= 0) return target
  }
  return undefined
}

// 実機比較用 (2026-09-06): PLAYER の既定値は保持し、受付条件だけ実行時に切り替える。
let stepCooldownOverrideMs: number | null = null
export function getStepCooldownMs(): number {
  return stepCooldownOverrideMs ?? PLAYER.stepCooldownMs
}
export function setStepCooldownMs(ms: number): void {
  if (!Number.isFinite(ms) || ms < 0 || ms > 2000) return
  stepCooldownOverrideMs = ms
}

// 毎tick ノックバック残距離を指数消費し x をスライドさせる (全state一様・PlayerState は変えない)。
//   CONSUME_PER_TICK = 0.28 → 鋭く入って減速するイーズアウト。SNAP で微小残を一気に消化し合計距離を正確に保つ。
const KNOCKBACK_CONSUME_PER_TICK = 0.28
const KNOCKBACK_SNAP_PX = 1
function applyKnockbackDrift(p: Player, bossId: string | undefined): Player {
  const rem = p.knockbackRemainingPx ?? 0
  if (rem === 0) return p.knockbackConsumePerTick === undefined ? p : { ...p, knockbackConsumePerTick: undefined }
  let step = rem * (p.knockbackConsumePerTick ?? KNOCKBACK_CONSUME_PER_TICK)
  let nrem = rem - step
  if (Math.abs(nrem) < KNOCKBACK_SNAP_PX) { step = rem; nrem = 0 }
  const b = arenaBoundsFor(bossId)
  const x = Math.max(b.leftWall, Math.min(b.rightWall, p.x + step))
  return { ...p, x, knockbackRemainingPx: nrem,
    ...(nrem === 0 && p.knockbackConsumePerTick !== undefined ? { knockbackConsumePerTick: undefined } : null) }
}

// 致命を弾いて押される (DEV ?execPush=new|sekiro・src/exec_parry.ts)。決めた進み方どおりに1コマずつ動かす。
//   壁までの残りは弾いた瞬間に計画へ入れてあるので、ここの壁の押さえは保険。
//   滑り終わった後も受けた姿 (parrying) の間は経過だけ進めて残す (描画の重い受けの姿の時計)。姿が変わったら捨てる。
function applyExecPushDrift(p: Player, bossId: string | undefined): Player {
  const plan = p.execPush
  if (!plan) return p
  if (!execPushSliding(plan) && p.state !== 'parrying') return { ...p, execPush: undefined }
  const { plan: next, dx } = stepExecPush(plan, FIXED_DT * 1000)
  if (dx === 0) return { ...p, execPush: next }
  const b = arenaBoundsFor(bossId)
  return { ...p, x: Math.max(b.leftWall, Math.min(b.rightWall, p.x + dx)), execPush: next }
}

function updatePlayerCore(player: Player, input: Input, boss: Boss, simTimeMs = 0): Player {
  // 各種減衰
  const decayedCounter = Math.max(0, player.counterWindow - FIXED_DT)

  let next: Player = {
    ...player,
    stateElapsed: player.stateElapsed + FIXED_DT,
    counterWindow: decayedCounter,
  }
  const elapsedMs = next.stateElapsed * 1000

  // タイマー終了処理
  if (player.state === 'attacking' && !playerComboEnabled()) {
    // 連撃なし (配布版の既定・player_combo_switch.ts): 攻撃中の押しは受けず、振り切ってから idle で受ける。
    if (elapsedMs >= totalAttackMs(0)) return finishAction(next, boss)
  } else if (player.state === 'attacking') {
    // 攻撃中の押しは覚えておき、振り終わり (時間割の totalMs・player_attack_timing.ts) に次の段を出す。
    //   パリィが押されていたら覚えた攻撃は捨て、下のパリィ切り替え (cancel窓) に任せる = 守りが常に優先。
    let queued = player.attackQueued === true
    if (input.pressed.has('attack')) {
      consume('attack')
      queued = true
    }
    if (input.pressed.has('parry')) queued = false
    // 回避の押しも覚えた攻撃を捨てる (今の振りは取り消さない)。 新しい守りの意図が古い攻撃の押しに勝つ。
    //   2026-10-02 ユーザー「連打で負けるのは正しい」= 連打で振りが繋がって被弾するのは正しいが、避けると決めた後まで次の段を出さない。
    if (input.pressed.has('step')) queued = false
    if (elapsedMs >= attackTimingOf(player).totalMs) {
      if (queued) return startAttackFromInput({ ...next, attackComboOpenUntilMs: simTimeMs }, boss, simTimeMs)
      // 最後まで振り切った時だけ、次の段へ繋がる受付を開く (パリィ等で途中を取り消した時は開かない)。
      return { ...finishAction(next, boss), attackComboOpenUntilMs: simTimeMs + PLAYER.attackComboGapMs }
    }
    next = { ...next, attackQueued: queued }
  }
  // パリィ長押しガード (トグル・既定ON・2026-07-20〜。`__guard(false)` で A/B 可): guardEnabled()+長押し中は、パリィ active 窓が終わった
  //   瞬間に guarding へ落とす。 弾き (active) を外しても無防備な recovery / whiff硬直を挟まず即・受け構えへ
  //   = 「パリィ失敗でも押しっぱなしなら守れる」(2026-07-07 ユーザー要望)。 素通り被弾の隙を消し、
  //   「失敗ほどガード到達が遅い」(whiff硬直上乗せ) の逆転も解消する。
  //   ※ active 中 (elapsed < startup+active) は弾き判定を優先して parrying を維持 (ここでは化けない)。
  //   ※ フラグOFF or 離した時は下の従来サイクル終了 (recovery+whiff硬直→idle) に落ちる=既存挙動不変。
  if (player.state === 'parrying'
      && guardEnabled() && input.hold.parry
      && elapsedMs >= PLAYER.parryStartupMs + player.parryActiveMsEffective
      // 弾き成立直後の新規パリィ入力は連続弾き再武装(下の deflect chain)を優先し、ガードへ化けさせない。
      //   = 「弾いた直後にもう一度タップ=次も弾き / 押しっぱなし=ガード」という本来の意図に一致させる。
      && !(input.pressed.has('parry') && player.parryAbsorbed)) {
    return enterGuarding(autoFace(next, boss))
  }
  // 空振りパリィ (1発も吸収せず終了) は parrying を少し延ばして硬直させる (連打抑止)。
  //   体幹罰・回復停止は課さない (空気を斬っただけで崩れるのは不自然 = plan A 2026-06-20)。
  const wastedStiffnessMs = player.parryAbsorbed ? 0 : PLAYER.wastedParryStiffnessMs
  // 致命を弾いて押されている間 (src/exec_parry.ts) は、滑り終わって少し経つまで受けた姿のまま
  //   (途中で idle へ戻すと、残りの滑りが「後ろ歩き」の絵で描かれて足が空回りする。歩きの判定は止まってから 0.15 秒残る)。
  const execPushHolding = player.execPushHoldUntilMs !== undefined && simTimeMs < player.execPushHoldUntilMs
  if (player.state === 'parrying' && !execPushHolding
      && elapsedMs >= PLAYER.parryStartupMs + player.parryActiveMsEffective + (player.parryCycleTailMs ?? 0) + PLAYER.parryRecoveryMs + wastedStiffnessMs) {
    // ここに来るのは「離した / フラグOFF」パスのみ (guardEnabled()+長押しは上で早期ガード済)。
    //   フェイルセーフとして guard 分岐は残す (上の早期ガードを将来動かしても崩れないよう)。
    if (guardEnabled() && input.hold.parry) {
      return enterGuarding(autoFace(next, boss))
    }
    return finishAction(next, boss)
  }
  if (player.state === 'stepping' && elapsedMs >= stepDurationMs()) {
    return finishAction(next, boss)
  }
  // 致命を弾ける仕組み (既定・旧は DEV ?execParry=off・src/exec_parry.ts): 崩れは致命が当たるか弾かれるまで続く。
  //   崩れの間はパリィのボタンだけ受け付ける (押した時刻を覚えるだけ・当たりの瞬間の判定は hits.ts)。回避・攻撃・歩きは効かない。
  //   致命が外れて戻りに入った時・上限 (保険) を過ぎた時は、今と同じ崩れ明け (体幹 0) にする。
  if (execStunActive(player, boss.id)) {
    if (input.pressed.has('parry')) {
      consume('parry')
      if (stunParryAccepts(player, simTimeMs)) next = { ...next, stunParryPressMs: simTimeMs }
    }
    const finisherMissed = isBossFinisherAttack(boss) && boss.state === 'recovery'
    if (finisherMissed || elapsedMs >= EXEC_PARRY.stunCapMs) {
      return { ...autoFace(enterIdle(next), boss), posture: 0 }
    }
    return next
  }
  if (player.state === 'stunned' && elapsedMs >= PLAYER.stunMs) {
    // 2026-07-06 ユーザー裁定: スタッガー明けは体幹フル回復 (旧=半分残し)。
    //   崩された直後に半分溜まったままだと連続崩しの負のスパイラルが重すぎる。
    return { ...autoFace(enterIdle(next), boss), posture: 0 }
  }
  // 致命の一撃: 3ヒット連続のフェーズ管理
  // 各フェーズ (240ms) ごとに executeHitIndex を進め、attackHitConsumed をリセット
  // 全 hits 終了 + endPoseMs 経過で idle に戻る
  if (player.state === 'executing') {
    // 直した版の普段の致命 (DEV ?lunaExec=swap2・src/luna_exec_swap.ts THRUST): 一突き1回だけ = 初めから3発目 (段階を閉じる当たり) として扱い、短い時計で終わる。
    //   両手で刺して引き抜く版 (DEV ?normalExecPose=pull・PULL) も同じく当たり1回で、抜きながら半歩下がる。
    const thrust = thrustTimingOn(player)
    const pull = pullTimingOn(player)
    const totalMs = thrust ? THRUST.totalMs : pull ? PULL.totalMs : EXECUTE.perHitDurationMs * EXECUTE.hits + EXECUTE.endPoseMs
    if (elapsedMs >= totalMs) {
      return autoFace(enterIdle(next), boss)
    }
    if ((thrust || pull) && player.executeHitIndex < EXECUTE.hits - 1) {
      next = { ...next, executeHitIndex: EXECUTE.hits - 1, attackHitConsumed: false }
    }
    const phaseEndMs = (player.executeHitIndex + 1) * EXECUTE.perHitDurationMs
    if (elapsedMs >= phaseEndMs && player.executeHitIndex < EXECUTE.hits - 1) {
      next = {
        ...next,
        executeHitIndex: next.executeHitIndex + 1,
        attackHitConsumed: false,
      }
    }
    // フィニッシャー致命: 開始直後 (~anchorEaseMs) で刺し距離へ smoothstep 吸着、 以降は固定 (足滑り防止)。
    if (next.executeAnchorX !== undefined && next.executeAnchorFromX !== undefined) {
      // 比較版 (DEV ?execGap・src/execute_spacing.ts) だけ: 1拍 (executePrepMs) を挟む時は、負の経過 → 0 の間に
      //   回避と同じ減速の曲線で寄せ切る。寄せる時間だけ延ばす時は executeAnchorEaseMs。配布版はどちらも undefined = 今のまま。
      const prepMs = next.executePrepMs ?? 0
      const easeMs = next.executeAnchorEaseMs ?? EXECUTE.anchorEaseMs
      const t = Math.max(0, Math.min(1, (elapsedMs + prepMs) / easeMs))
      const s = prepMs > 0 ? spacingPrepProgress(t) : t * t * (3 - 2 * t)
      next = { ...next, x: next.executeAnchorFromX + (next.executeAnchorX - next.executeAnchorFromX) * s }
    }
    // 引き抜く版: 下がった量だけ後ろへ (寄せがある時は毎回の位置から・無い時は前の手番からの差だけ)
    if (pull) {
      const back = next.facing === 'right' ? -1 : 1
      const anchored = next.executeAnchorX !== undefined && next.executeAnchorFromX !== undefined
      const dx = pullStepBackPx(elapsedMs) - (anchored ? 0 : pullStepBackPx(player.stateElapsed * 1000))
      next = { ...next, x: next.x + back * dx }
    }
    // executing 中は移動・入力ロック (anchor 吸着以外は動かさない)
    return next
  }

  // 掴み (処刑人 D8/D9): holdMs 経過で解放 (damage 適用 + releaseKnockbackPx + idle 復帰)。
  //   位置は executeAnchor と同じテンプレ: anchorFromX→anchorX へ EXECUTE.anchorEaseMs で
  //   smoothstep 吸着 (瞬間ワープ禁止)。 grabbed 中は完全な入力ロック (anchor 吸着以外は動かさない)。
  if (player.state === 'grabbed') {
    const g = player.grab
    if (!g) return next
    const t = Math.min(1, elapsedMs / (g.pullMs ?? EXECUTE.anchorEaseMs))
    const s = t * t * (3 - 2 * t)
    next = { ...next, x: g.anchorFromX + (g.anchorX - g.anchorFromX) * s }
    if (elapsedMs >= g.holdMs) {
      // 解放: 既存の被ダメ経路と同じ形で damage を適用 (hurtVisual/hurtNonce/postureRegenLock)
      //   → ボスから離れる向き (dir) へ releaseKnockbackPx → idle へ。
      //   HP0 は既存の defeat 経路にそのまま乗せる (チュートリアル無敵は hpFloor=1 で維持)。
      // 傷を負わせない掴み (剣舞の引き寄せ = 布で手もとへ寄せるだけ) は、被弾の赤い光や音を出さずに放す。
      if (g.damage <= 0) return { ...autoFace(enterIdle(next), boss), grab: undefined }
      const hpFloor = boss.protectFromDeath ? 1 : 0
      // 掴みは致命ではないため bossFinisher フラグは渡さない (票 §3-b)。
      const newHp = Math.max(hpFloor, next.hp - playerIncomingDamage(g.damage))
      return {
        ...autoFace(enterIdle(next), boss),
        hp: newHp,
        hurtVisualUntil: simTimeMs / 1000 + 0.4,
        hurtNonce: next.hurtNonce + 1,
        parryChain: 0,
        lastParryTime: null,
        postureRegenLockSec: Math.max(next.postureRegenLockSec, PLAYER.hitRegenLockSec),
        knockbackRemainingPx: g.dir * g.releaseKnockbackPx,
        grab: undefined,
      }
    }
    return next
  }

  // ガード (パリィ長押し・トグル・既定ON・2026-07-20〜。`__guard(false)` で A/B 可): guardEnabled() の時だけ到達する状態。
  //   離す (hold.parry===false) か 無効化されたら即 idle へ (spec 1「離すと即解除」)。
  //   ガード中は攻撃/ステップ/移動不可 (spec 5・最小構成)。 facing だけボス側へ更新して張り付く。
  //   被弾解決 (チップ+自体幹) は hits.ts のガード分岐。 体幹の自然回復停止は posture.ts。
  if (player.state === 'guarding') {
    if (!guardEnabled() || !input.hold.parry) {
      return autoFace(enterIdle(next), boss)
    }
    return { ...next, facing: boss.x < next.x ? 'left' : 'right' }
  }

  // ターゲットカメラ風: ボス方向を自動で向く (stepping/stunned中は固定)
  //   見切り反撃の突き・連撃3段目の踏み込みで滑っている間も固定 (2026-10-18): どちらも facing の向きへ進むので、
  //   ボスの中心を越えた瞬間に向き直すと次のコマで逆へ滑り、中心をはさんで毎コマ往復していた
  //   (芯の止め幅 lungeStopGapPx が無い覚醒王・処刑人で目立つ。反撃1回につき3〜4コマ震える)。
  if (player.state !== 'stepping' && player.state !== 'stunned' && !isLungeSliding(next) && !(swingFacingLocked() && next.state === 'attacking')) {
    next = { ...next, facing: boss.x < next.x ? 'left' : 'right' }
  }

  // ---- 連続弾き (deflect chain): 弾きが成立した瞬間だけ、次のパリィを即・フル窓で出せる ----
  //   SEKIRO 型。 成功 (parryAbsorbed) 時限定 = 空振り/neutral 連打には開かない (mash 窓縮み罰は据え置き
  //   なので「連打正義」にならない)。 parrying の active+recovery(320ms) を待たず再武装できる →
  //   250ms 間隔の高速連 (裾乱れ) をパリィ主体で捌ける (P1「手の解放」の 350ms 前提を超える速さへの対応)。
  //   再パリィ (enterParrying) は parryAbsorbed を false に戻すので「次の一撃を実際に弾けた時だけ続く」
  //   = 弾き損ねたら普通の recovery/whiff 硬直に戻る = 無限連打にならない。 通常/ジャスト両方が対象
  //   (parryAbsorbed は hits.ts で両方に立つ)。
  if (
    input.pressed.has('parry') &&
    player.state === 'parrying' &&
    player.parryAbsorbed
  ) {
    consume('parry')
    return enterParrying(autoFace(next, boss), simTimeMs, true)
  }

  // ---- cancel窓: 攻撃/ダッシュの硬直をパリィでキャンセル (P1「手の解放」ブループリント §2.1) ----
  //   攻撃は「振る前なら取り消せる / 振った直後だけは判断に責任を持つ / 後半recoveryは弾ける」。
  //   攻撃を当てた利益と直後の防御を毎回両取りする連打を防ぎつつ、見て止める自由と
  //   パリィ主体の速さは残す。パリィ入力でのみキャンセル可。
  //   ダッシュ→ダッシュはチェーンさせない。 攻撃→攻撃は連撃の繋ぎ (attackComboChainMs・関数冒頭) で出る。
  if (
    input.pressed.has('parry') &&
    // 攻撃: 振る前、または短いcommit区間が終わった後。active〜命中直後は攻撃を完遂する。
    ((player.state === 'attacking'
      && (elapsedMs < attackTimingOf(player).activeStartMs || elapsedMs >= attackTimingOf(player).parryReopenMs)) ||
      // ダッシュ: i-frame 終了後のみ。回避の硬直撃ちを「操作」で返せるように。
      (player.state === 'stepping' && elapsedMs >= playerStepIFrameMs()))
  ) {
    consume('parry')
    return enterParrying(autoFace(next, boss), simTimeMs)
  }
  // 長い回避 (DEV ?dodgeLen=b|c|d) だけ: 見切りの窓が開いていれば、無敵が明けた後の攻撃で回避の残りを取り消して反撃へ。
  //   10-06 ユーザー (B・C を実機で)「回避反撃が出ないな」= 回避中の攻撃の先行入力 (5コマ) が回避の終わりまで残らず、
  //   C は終わった時に窓 (550ms) もほぼ残っていなかった。 普通の攻撃は今まで通り回避の終わりまで待つ。 今の回避 (260ms) は変えない。
  //   無敵の間に押した攻撃は覚えておき、無敵が明けた瞬間に出す (窓が開いて光った直後に押しても消えない)。
  if (player.state === 'stepping' && stepDurationMs() > PLAYER.stepDurationMs && next.counterWindow > 0) {
    if (input.pressed.has('attack')) {
      consume('attack')
      next = { ...next, attackQueued: true }
    }
    if (input.pressed.has('step')) next = { ...next, attackQueued: false }
    if (next.attackQueued && elapsedMs >= playerStepIFrameMs()) return startAttackFromInput({ ...next, attackQueued: false }, boss, simTimeMs)
  }

  if (player.state !== 'idle') {
    if (player.state === 'stepping') {
      return moveDuringStep(next, boss)
    }
    if (player.state === 'attacking' && player.attackIsCounter) {
      return moveDuringCounterThrust(next, boss)
    }
    if (player.state === 'attacking' && isHeavyComboStep(player)) {
      return moveDuringHeavyLunge(next, boss)
    }
    return next
  }

  // ---- idle中の入力処理 ----
  if (input.pressed.has('parry')) {
    consume('parry')
    return enterParrying(next, simTimeMs)
  }
  // 回避の連打対策 (2026-09-06・docs/candidates/2026-09-06-dodge-spam-fable-design.md): 縛るのは
  //   回避→回避だけ。 クールダウン中に拒否しても consume せず・return もしない = 同tickの歩き/攻撃/パリィは
  //   そのまま出る (下の attack 判定・末尾の歩き処理へ落ちる)。 83ms 以内の早押しは入力バッファで自動的に出る。
  if (input.pressed.has('step') && simTimeMs - next.lastStepStartMs >= getStepCooldownMs()) {
    consume('step')
    const stepDir = input.moveX !== 0 ? Math.sign(input.moveX) : (boss.x > next.x ? -1 : 1)
    return enterStepping(next, stepDir, simTimeMs, boss)
  }
  if (input.pressed.has('attack')) {
    consume('attack')
    return startAttackFromInput(next, boss, simTimeMs)
  }

  // 身体の通過そのものには無敵を付けない。牙・武器の authored hitbox とステップ固有 i-frame だけが
  // 被弾可否を決める。
  // 🔴 2026-07-27: **歩きだけ** ボスの「芯」で止まる (balance.ts BOSS_CORE)。
  //   回避 (stepping) はこの枝を通らない = 従来どおり通り抜ける = 背中を取るには回避を1回払う。
  // 🔵 2026-09-06 便1「芯の中では立ち止まれない」(docs/candidates/2026-09-06-boss-core-real-size-fable-design.md §4):
  //   回避で芯の中へ入った後、idle/歩きの tick に来たら入力を無視して今いる側の縁へ歩き速度で滑り出る。
  //   外へ縁を越えたら縁で止める。中心へ向く入力は無視 = 居座れない (押し出しはしない・段差禁止)。
  //   ⚠️ 被弾ノックバック中 (knockbackRemainingPx≠0) は動かさない (§4-2 5.「行動中は動かさない」の一部。
  //   ここで動かすと applyKnockbackDrift の変位に上乗せされ、ノックバック距離が過剰になる・2026-09-06実測で発見)。
  // ⚠️ 実装ノート (2026-09-06・設計書に無い注意点): この判定 (パリィ/回避/攻撃の後) より前に置くと、
  //   「毎tick回避を押し続ける」入力 (安置bugの再現bot=dodgeClingBot・回避連打+密着bot) の場合、
  //   stepping が明けた同じ idle tick にまた 'step' が即消費されて滑りが一度も発火しない
  //   (実測: 施策前後で被弾/生存が完全に一致)。 逆に「行動選択より先に滑らせる」と、回避の開始位置が
  //   1tickぶんズレて回避の総移動距離が90pxからズレる (実測: 90→88px。tests/front_exchange_integrity
  //   「全ボスのステップは共通90pxのまま」を壊す＝間合いの不変条件)。 どちらも壊さずに両立させる案は
  //   Player に「回避を跨いで芯ドリフトを積む」ような追加状態を要り、便1の範囲を超えるため見送った。
  //   → 回避を一度も緩めない (idle へ全く戻らない) 入力に対しては、この便では居座り対策が効かない
  //   (歩き越しの侵入・弾の再計算・停止位置43→71自体は効く)。 残課題として報告する。
  // DEV ?lunaWalk=new|newslow (2026-10-12・src/player_walk_new.ts) だけ: 動き出しに 0.07 秒の加速。 離したらすぐ止まる。 配布版は今のまま (加速なし)
  const accel = lunaWalkAccel()
  const walkV = accel ? nextWalkVelocity(player.walkVx ?? 0, input.moveX, walkSpeedPxPerSec(), FIXED_DT) : input.moveX * walkSpeedPxPerSec()
  const x = ((player.knockbackRemainingPx ?? 0) !== 0 || execPushSliding(player.execPush) ? null : slideOutOfBossCore(next, boss))
    ?? blockedByBossCore(next.x, next.x + walkV * FIXED_DT, boss)
  const nx = clampToArena(x, boss.id)
  // 持つのは実際に進んだ速さ (芯や壁で止められたら 0 = 次の tick は加速し直す)。 描画はこれで歩いているかを決める
  return accel ? { ...next, x: nx, walkVx: (nx - next.x) / FIXED_DT } : { ...next, x: nx }
}

// 芯の中に居る間だけ、今いる側の縁へ歩き速度で滑り出す位置を返す。 外に居る時は null (呼び出し側が
//   従来の blockedByBossCore=「外からは入れない」へフォールバックする)。
//   §4-2: 外向き成分は sign(player.x-boss.x)。
//   🔵 設計書は |dx|<0.5 (ちょうど中心) の時 stepDir/facing の反対で仮の向きを決める案だったが、
//   実装では**何もしない**に変えた (2026-09-06)。理由=芯が実寸化される前の全ボス既定28でも境界値
//   テスト (tests/combat.test.ts「boss lunge」offset=0) が「静止した的に正面から当たる」ことを検証しており、
//   ここで任意の向きへ滑らせると、たまたま選んだ向きがボスの攻撃方向と逆 (=背後) になった時に
//   被弾テストの結果が変わってしまう (実測)。 |dx|<0.5 は連続的な物理でまず起きない (回避90pxの
//   どこにも一致しない) ので、「動かさない」に倒しても居座り対策としての実効は失わない。
function slideOutOfBossCore(next: Player, boss: Boss): number | null {
  // 獣比較 (bodyModel=beast): 内側は自由。居座りは獣自身の移動 (空振りの勢い・向き直り) が解く。
  if (!bossCoreEnabled || beastBody(boss)) return null
  const minGap = stanceBodyGap(boss, next.x - boss.x) ?? (bossCoreHalfWidth(boss) + BOSS_CORE.playerHalfWidth)
  const rawDx = next.x - boss.x
  if (Math.abs(rawDx) >= minGap || Math.abs(rawDx) < 0.5) return null
  const outward = Math.sign(rawDx)
  const moved = next.x + outward * PLAYER.moveSpeed * FIXED_DT
  const edge = boss.x + outward * minGap
  return outward > 0 ? Math.min(moved, edge) : Math.max(moved, edge)
}

export function updatePlayer(player: Player, input: Input, boss: Boss, simTimeMs = 0): Player {
  const out = applyExecPushDrift(applyKnockbackDrift(updatePlayerCore(player, input, boss, simTimeMs), boss.id), boss.id)
  // 歩きの速さ (DEV ?lunaWalk=new|newslow だけが持つ) は歩いていない間は 0 に戻す (攻撃・回避の後に古い速さで歩き出さない)
  return out.walkVx && out.state !== 'idle' ? { ...out, walkVx: 0 } : out
}

function autoFace(player: Player, boss: Boss): Player {
  return { ...player, facing: boss.x < player.x ? 'left' : 'right' }
}

// 致命を開始できるか: 崩し中の間合い判定 (向きは enterExecuting/autoFace が自動補正するため距離のみ)。
//   approachRange 内なら踏み込み致命として発動。それ以外は通常攻撃へフォールバック。
function canStartExecute(player: Player, boss: Boss): boolean {
  return Math.abs(boss.x - player.x) <= executeApproachRangeFor(boss.bodyHalfWidthPx)
}

// アクション終了時の共通処理: 常に idle へ (スタミナ廃止 2026-06-17)
function finishAction(player: Player, boss: Boss): Player {
  return autoFace(enterIdle(player), boss)
}

// 攻撃ボタンで出る行動 (idle から・連撃の繋ぎから共通)。
//   致命の一撃: ボス体幹崩し中 かつ 間合い内 のときだけ発動。
//   遠距離からの空振り致命 (崩しを無駄撃ち) を防ぐ (Codex 監査 B)。 間合い外は通常攻撃へ落とす。
function startAttackFromInput(player: Player, boss: Boss, simTimeMs: number): Player {
  if (boss.state === 'staggered' && canStartExecute(player, boss)) return enterExecuting(player, boss)
  return enterAttack(autoFace(player, boss), player.counterWindow > 0, simTimeMs)
}

// 連撃: 前の攻撃が振り切られてから attackComboGapMs 以内なら次の段。 見切りカウンターは段に数えず
//   1段目扱い (カウンターの後に続けて押すと2段目が出る)。
function enterAttack(player: Player, isCounter: boolean, simTimeMs: number): Player {
  const chained = playerComboEnabled() && !isCounter && simTimeMs <= (player.attackComboOpenUntilMs ?? -Infinity)
  const attackComboStep = chained ? ((player.attackComboStep ?? 0) + 1) % PLAYER.attackComboSteps : 0
  return {
    ...player,
    state: 'attacking',
    stateElapsed: 0,
    attackHitConsumed: false,
    attackContactKind: null,
    attackIsCounter: isCounter,
    attackComboStep,
    attackComboOpenUntilMs: -9999,
    attackQueued: false,
  }
}

/** 連撃の締め (3段目) か。 見切りカウンターは段に数えない。 */
export function isHeavyComboStep(player: Player): boolean {
  return !player.attackIsCounter && (player.attackComboStep ?? 0) === PLAYER.attackComboSteps - 1
}

// 致命開始時に「この致命で撃破できるか」で見た目バリアントを決める。
// 部屋制 (2026-06-11): この致命が「最後の部屋」を閉じるなら finisher (撃破確定)、
//   それ以外は normal (部屋を1つ閉じて戦闘続行)。 旧判定 (HP<=合計40) は部屋制で置換。
function enterExecuting(player: Player, boss: Boss): Player {
  const variant = boss.executesTaken + 1 >= Math.max(1, boss.roomCount) ? 'finisher' : 'normal'
  // anchor: プレイヤーを刺し距離へ anchorEaseMs で短く寄せる (足滑り防止・ワープに見えない速度)。
  //   finisher = ボス固有の finisherAnchorOffset (reach 判定をスキップするため距離を問わず使用)。
  //   normal   = reach 内に必ず収める offset = min(finisherAnchorOffset, reach-10)。
  //              dist ≤ reach ならアンカー不要 (既に届いている)。
  let executeAnchorX: number | undefined
  let executeAnchorFromX: number | undefined
  let executePrepMs: number | undefined
  let executeAnchorEaseMs: number | undefined
  const dist = Math.abs(boss.x - player.x)
  const sign = player.x <= boss.x ? -1 : 1
  // 途中の致命でも、近すぎる時は外へ寄せる (2026-10-10 採用・src/execute_spacing.ts)。旧 = DEV ?execGap=0 (下の決まりのまま)。
  const spacing = variant === 'normal' ? planExecuteSpacing(player.x, boss, (x) => clampToArena(x, boss.id)) : undefined
  if (spacing) {
    executeAnchorX = spacing.anchorX
    executeAnchorFromX = spacing.fromX
    executePrepMs = spacing.prepMs > 0 ? spacing.prepMs : undefined
    executeAnchorEaseMs = spacing.easeMs
  } else if (variant === 'finisher') {
    // 内側から大きく外へ寄せる時は、途中の致命と同じ「間合いを取る1拍」を挟む (2026-10-18 採用・src/execute_spacing.ts)。旧 = DEV ?finisherGap=0 (いつでも 0.12 秒の寄せ)。
    const fin = planFinisherSpacing(player.x, boss, (x) => clampToArena(x, boss.id))
    executeAnchorX = fin?.anchorX ?? clampToArena(boss.x + sign * boss.finisherAnchorOffset, boss.id)
    executeAnchorFromX = player.x
    if (fin) { executePrepMs = fin.prepMs; executeAnchorEaseMs = fin.easeMs }
  } else if (dist > executeReachFor(boss.bodyHalfWidthPx)) {
    // 踏み込み致命: reach 以内に確実に着地させ全3ヒットが命中するよう。
    const normalAnchorOffset = Math.min(boss.finisherAnchorOffset, executeReachFor(boss.bodyHalfWidthPx) - 10)
    executeAnchorX = clampToArena(boss.x + sign * normalAnchorOffset, boss.id)
    executeAnchorFromX = player.x
  }
  return {
    ...player,
    state: 'executing',
    // 1拍を挟む時 (比較版のみ) は負から始める = 3発の時計 (0 から) はそのまま後ろへずれる。配布版は常に 0。
    stateElapsed: executePrepMs ? -executePrepMs / 1000 : 0,
    attackComboOpenUntilMs: -9999,
    attackHitConsumed: false,
    attackIsCounter: false,
    parryAbsorbed: false,
    executeHitIndex: 0,
    executeVariant: variant,
    executeAnchorX,
    executeAnchorFromX,
    executePrepMs,
    executeAnchorEaseMs,
  }
}

function enterParrying(player: Player, simTimeMs: number, chained = false): Player {
  const dt = simTimeMs - player.lastParryPressMs
  // 連続弾き (chained) = 弾きを当てた直後の即・再パリィ。 mash ではない (成功の褒美) ので
  //   窓縮みを課さずフル窓で再武装させる。 通常 (idle/キャンセル窓) 入力だけ mash 対象。
  const mashCount = chained ? 0 : dt < PLAYER.mashThresholdMs ? player.parryMashCount + 1 : 0
  const fullEffective = Math.max(
    PLAYER.parryFloorMs,
    playerParryActiveMs() - mashCount * PLAYER.parryShrinkPerPress,
  )
  // パリィは1種類 (既定・src/parry_one.ts): 受付だけ 120ms に縮める。DEV ?parryOne=off は旧 (受付はそのまま)。パリィ1回の長さ (空振りの硬直・動ける時刻) は縮めない = 足りない分を tail で足す。
  const effective = parryOneCatchMs(fullEffective)
  return {
    ...player,
    state: 'parrying',
    stateElapsed: 0,
    attackComboOpenUntilMs: -9999,
    attackHitConsumed: false,
    parryAbsorbed: false,
    lastParryPressMs: simTimeMs,
    parryMashCount: mashCount,
    parryActiveMsEffective: effective,
    ...(parryOneOn() ? { parryCycleTailMs: parryOneTailMs(fullEffective) } : null),
  }
}

// パリィ長押しガードへ移行 (トグル・デフォルトOFF)。 enterParrying の直後 (サイクル終了時) にだけ呼ばれる。
//   enterIdle と同じく攻撃/カウンター/吸収フラグをリセットして「構え直した」状態にする。
function enterGuarding(player: Player): Player {
  return {
    ...player,
    state: 'guarding',
    stateElapsed: 0,
    attackComboOpenUntilMs: -9999,
    attackHitConsumed: false,
    attackIsCounter: false,
    parryAbsorbed: false,
  }
}

function enterStepping(player: Player, dir: number, simTimeMs: number, boss: Boss): Player {
  const crossing = bossCoreEnabled ? stanceDodgeLanding(player.x, dir, boss) : undefined
  const targetX = crossing ?? coreStepLanding(player.x, dir, boss)
  // facing は敵を見据えたまま固定 (呼び出し前に boss 方向へ設定済み)。
  // 移動方向は stepDir に保持し、facing と独立させる (回避中に背を向けないため)。
  return {
    ...player,
    state: 'stepping',
    stateElapsed: 0,
    attackComboOpenUntilMs: -9999,
    attackHitConsumed: false,
    stepDir: dir,
    lastStepStartMs: simTimeMs,
    attackQueued: false,
    coreStepLanding: targetX === undefined ? undefined : { fromX: player.x, targetX,
      ...(crossing === undefined ? {} : { stanceCrossing: true }) },
  }
}

function enterIdle(player: Player): Player {
  return {
    ...player,
    ...(player.stunParryPressMs !== undefined ? { stunParryPressMs: undefined } : null),
    state: 'idle',
    stateElapsed: 0,
    attackHitConsumed: false,
    attackContactKind: null,
    attackIsCounter: false,
    parryAbsorbed: false,
    executeVariant: 'normal',
  }
}

function moveDuringStep(next: Player, boss: Boss): Player {
  if (next.coreStepLanding) {
    if (next.coreStepLanding.stanceCrossing) {
      // Full dodge duration, smooth acceleration/deceleration; no end-of-action
      // correction. Interruption leaves the player at the reached position.
      //   回避 d (移動 200ms) でも、体を通り抜ける長い移動は旧の 260ms のまま (1コマの跳びを 40px 未満に保つ)。
      const duration = Math.floor(Math.max(dodgeTravelMs(), PLAYER.stepDurationMs) / (FIXED_DT * 1000)) * FIXED_DT
      const ease = (s: number) => { const t = Math.max(0, Math.min(1, s / duration)); return t * t * (3 - 2 * t) }
      const delta = ease(next.stateElapsed) - ease(next.stateElapsed - FIXED_DT)
      const { fromX, targetX } = next.coreStepLanding
      return { ...next, x: clampToArena(next.x + (targetX - fromX) * delta, boss.id) }
    }
    // パリィで回避をキャンセルできる最初のtickより前に着地を終える。
    // 無敵・回避の尺・パリィの受付は変えず、終了後に位置を吸着させない。
    const lastMoveMs = (Math.ceil(playerStepIFrameMs() / (FIXED_DT * 1000)) - 1) * FIXED_DT * 1000
    const t = Math.min(1, next.stateElapsed * 1000 / lastMoveMs)
    const previousT = Math.min(1, Math.max(0, (next.stateElapsed - FIXED_DT) * 1000 / lastMoveMs))
    const { fromX, targetX } = next.coreStepLanding
    // 差分だけ加える。回避中に受けたノックバックを絶対座標で巻き戻さない。
    return { ...next, x: clampToArena(next.x + (targetX - fromX) * (t - previousT), boss.id) }
  }
  const stepDir = next.stepDir ?? (next.facing === 'right' ? 1 : -1)
  const customTravelSec = Math.floor(dodgeTravelMs() / (FIXED_DT * 1000)) * FIXED_DT
  // state終了判定より実際に移動するtick数で割り、通常ステップも設定値どおり90px進ませる。
  //   今の版は等速。 長い版 (DEV ?dodgeLen=b|c) は最初に大きく進んで着地へ減速する (合計は同じ 90px)。
  //   d は移動を最初の 200ms で終え、 残りは止まって起き上がる (dodgeTravelMs)。
  const travelDistance = PLAYER.stepDistance
  const delta = dodgeTravelProgress(next.stateElapsed / customTravelSec) - dodgeTravelProgress((next.stateElapsed - FIXED_DT) / customTravelSec)
  let x = next.x + stepDir * travelDistance * delta
  x = clampToArena(x, boss.id)
  return { ...next, x }
}

// 突き・踏み込みで facing の向きへ滑っている最中か (この間は自動の向き直しを止める)
function isLungeSliding(player: Player): boolean {
  if (player.state !== 'attacking') return false
  const ms = player.stateElapsed * 1000
  if (player.attackIsCounter) return ms >= PLAYER.attackActiveStartMs && ms < PLAYER.attackActiveEndMs
  if (isHeavyComboStep(player)) return ms <= attackTimingOf(player).activeEndMs + FIXED_DT * 500
  return false
}

function moveDuringCounterThrust(next: Player, boss: Boss): Player {
  const ms = next.stateElapsed * 1000
  if (ms < PLAYER.attackActiveStartMs || ms >= PLAYER.attackActiveEndMs) {
    return next
  }
  const dir = next.facing === 'right' ? 1 : -1
  return moveForwardStoppingAtCore(next, next.x + dir * PLAYER.thrustSlideSpeed * FIXED_DT, boss)
}

// 連撃の締め (3段目): 振り始めから当たりの窓の終わりまでに attackHeavyLungePx だけ前へ踏み込む。
//   ゆっくり出て速く入る smoothstep。 ボスの芯の扱いは見切り突進と同じ (外から来たら手前で止まる)。
function moveDuringHeavyLunge(next: Player, boss: Boss): Player {
  const durationSec = attackTimingOf(next).activeEndMs / 1000
  if (next.stateElapsed > durationSec + FIXED_DT / 2) return next
  const step = next.attackComboStep ?? 0
  const delta = heavyLungeProgress(step, next.stateElapsed * 1000) - heavyLungeProgress(step, (next.stateElapsed - FIXED_DT) * 1000)
  const dir = next.facing === 'right' ? 1 : -1
  return moveForwardStoppingAtCore(next, next.x + dir * PLAYER.attackHeavyLungePx * delta, boss)
}

function moveForwardStoppingAtCore(next: Player, target: number, boss: Boss): Player {
  let x = target
  // 🔴 2026-07-27: 突進も芯の手前で止める。 回避 (stepping) は従来どおり通す = 背中を取る
  //   手段は「回避1回の対価」のまま。 突進は 1500px/s = 歩きの25倍で、そこだけ芯が無いと
  //   「見切ったら勝手に裏へ抜ける」= 対価を払わずに背中が取れていた。
  if (BOSS_CORE.stopCounterThrust) {
    // 🔵 2026-09-06 便1 §4-2-4: 外から来た突進は従来どおり手前で止める。 既に芯の内側から
    //   始めた突進 (回避で裏へ抜けた直後など) は「中心向きの移動をしない」だけに変える
    //   (技自体は出る・reach は届く)。 外向きの移動はそのまま通す = 居座れる穴を閉じる。
    // 🔵 bodyModel=ghost&thrustStop=1 (2026-09-18 比較): 歩き・回避は通り抜けても、突進だけは芯で止める。
    const thrustCore = bossCoreEnabled || thrustStopsWithoutCore
    const startedInside = thrustCore && Math.abs(next.x - boss.x) < (stanceBodyGap(boss, next.x - boss.x) ?? (bossCoreHalfWidth(boss) + BOSS_CORE.playerHalfWidth))
    if (startedInside) {
      const movesInward = Math.abs(x - boss.x) < Math.abs(next.x - boss.x)
      if (movesInward) x = next.x
    } else {
      x = blockedByBossCore(next.x, x, boss, thrustCore)
    }
  }
  x = stopAtCounterGap(next.x, x, boss)
  x = clampToArena(x, boss.id)
  return { ...next, x }
}

// 見切り反撃・連撃3段目の踏み込みを「剣先が胴へ刺さる距離」で止める (2026-10-06 騎士の重なり対策・案A)。
//   身体判定なしのまま、歩き・回避は通り抜ける。止めるのは前進する突進だけ:
//   外から来た突進は ボス中心から gap の手前で止まり (追い越さない)、既に gap の内側から始めた突進は
//   「中心へ近づく移動をしない」だけ (外向きは通す = 閉じ込めない・技自体は出て reach は届く)。
//   gap は bosses.ts lungeStopGapPx (未指定のボスは止めない)。DEV ?counterStop=off|<px> で上書き。
let counterStopOverride: 'off' | number | null = null
export function setCounterStopGap(v: 'off' | number | null): void { counterStopOverride = v }
function counterStopGapFor(boss: Boss): number {
  if (counterStopOverride === 'off') return 0
  if (typeof counterStopOverride === 'number') return counterStopOverride
  return bossCounterStopGap(boss)
}
function stopAtCounterGap(fromX: number, toX: number, boss: Boss): number {
  const gap = counterStopGapFor(boss)
  if (gap <= 0) return toX
  const side = Math.sign(fromX - boss.x) || 1
  const fromRel = (fromX - boss.x) * side
  const toRel = (toX - boss.x) * side
  if (fromRel < gap) return toRel < fromRel ? fromX : toX   // 内側から: 近づく動きだけ止める
  return toRel >= gap ? toX : boss.x + side * gap            // 外から: gap の手前で止める
}

// 🔵 検討して不採用にした「突進専用のより厳しい芯」(2026-07-27):
//   歩き用と違い内側スタートでも「今より深くしない・追い越さない」まで縛る案。
//   実測で 裏返り 2.1→1.8回/戦 と僅差にしかならず、処刑人の勝率回帰 (8/20) も
//   entry-only 版と完全に同じだった = 規則を2つに増やす価値が無いので歩き用を再利用する。
//   復活させるなら git 履歴 (このcommitの親) にある。

// ボスの「芯」で歩きを止める (2026-07-27)。 押し返さない = めり込んでいる時はその場に留まるだけ。
//   ⚠️ 既に芯の内側に居る時 (回避で入った / ボスに踏み込まれた) は動きを妨げない。
//      押し出すと「ボスに弾かれる」不自然さが出るうえ、回避で入った意味が消える。
//      内側から外へ出る動きは常に許す = 閉じ込めない。
function blockedByBossCore(fromX: number, toX: number, boss: Boss, enabled = bossCoreEnabled): number {
  if (!enabled) return toX
  // Walking and counter movement share the same stable body boundary.
  const minGap = stanceBodyGap(boss, fromX - boss.x) ?? (bossCoreHalfWidth(boss) + BOSS_CORE.playerHalfWidth)
  const wasOutside = Math.abs(fromX - boss.x) >= minGap
  if (!wasOutside) return toX                       // 内側スタート = 自由 (出るも留まるも)
  const staysOutside = stanceBodyGap(boss, fromX - boss.x) === undefined
    ? Math.abs(toX - boss.x) >= minGap
    : (toX - boss.x) * Math.sign(fromX - boss.x) >= minGap
  if (staysOutside) return toX   // 外側のまま = そのまま
  return boss.x + Math.sign(fromX - boss.x) * minGap // 芯の手前で止める
}

let bossCoreEnabled: boolean = BOSS_CORE.enabled
export function setBossCore(on: boolean): void { bossCoreEnabled = on }
// 芯を切っていても見切り突進だけは芯で止める (DEV 比較 thrustStop=1)。芯ありの時は無関係。
let thrustStopsWithoutCore = false
export function setCounterThrustStop(on: boolean): void { thrustStopsWithoutCore = on }
export function isBossCore(): boolean { return bossCoreEnabled }

function clampToArena(x: number, bossId: string | undefined): number {
  const b = arenaBoundsFor(bossId)
  return Math.max(b.leftWall, Math.min(b.rightWall, x))
}

export function totalAttackMs(comboStep = 0): number {
  return attackStepTiming(comboStep).totalMs
}
export function totalParryMs(): number {
  return PLAYER.parryStartupMs + PLAYER.parryActiveMs + PLAYER.parryRecoveryMs
}

// ------- 公開ヘルパ -------
export function isPlayerAttackActive(player: Player): boolean {
  if (player.state !== 'attacking') return false
  const ms = player.stateElapsed * 1000
  const t = attackTimingOf(player)
  return ms >= t.activeStartMs && ms < t.activeEndMs
}
export function isPlayerParryActive(player: Player): boolean {
  if (player.state !== 'parrying') return false
  const ms = player.stateElapsed * 1000
  return ms >= PLAYER.parryStartupMs && ms < PLAYER.parryStartupMs + player.parryActiveMsEffective
}
export function isPlayerInIFrames(player: Player): boolean {
  if (player.state !== 'stepping') return false
  const ms = player.stateElapsed * 1000
  return ms < playerStepIFrameMs()
}
export function isPlayerStunned(player: Player): boolean {
  return player.state === 'stunned'
}
export function stunPlayer(player: Player, cause?: Player['stunCause']): Player {
  return { ...player, state: 'stunned', stateElapsed: 0, attackHitConsumed: false, attackComboOpenUntilMs: -9999, stunCause: cause,
    ...(player.stunParryPressMs !== undefined ? { stunParryPressMs: undefined } : null) }
}
// 掴み成立 (処刑人 D8/D9): hits.ts が melee ヒット成立時に呼ぶ。 payload の解放処理は
//   updatePlayerCore の 'grabbed' 分岐 (holdMs 満了で damage+knockback+idle 復帰)。
export function grabPlayer(player: Player, payload: PlayerGrabState): Player {
  return { ...player, state: 'grabbed', stateElapsed: 0, attackHitConsumed: false, grab: payload, attackComboOpenUntilMs: -9999 }
}
export function openCounterWindow(player: Player): Player {
  return { ...player, counterWindow: PLAYER.counterWindowMs / 1000 }
}
export function isInCounterWindow(player: Player): boolean {
  return player.counterWindow > 0
}
// 攻撃を吸収したパリィであることを記録 (空振り罰回避用)
export function markParryAbsorbed(player: Player): Player {
  return { ...player, parryAbsorbed: true }
}
